import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  BarChart3, Coins, ArrowDownCircle, Hourglass, ShieldCheck, RefreshCw,
  Crown, Phone, Clock, AlertTriangle, Users,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Admin → Points → Analytics  (Phase 4)
//
// Three sections:
//   1. KPI cards  : Issued / Redeemed / Expired today + Active Liability
//   2. Top Users  : highest active points
//   3. Expiring   : ACTIVE credits expiring inside a configurable window
//
// All numbers are computed LIVE on the backend from `wallet_transactions`;
// the frontend is a pure read view with skeleton / empty / refresh states.
// ─────────────────────────────────────────────────────────────────────────────

const WINDOW_OPTIONS = [
  { value: 30,   label: 'Next 30 min' },
  { value: 60,   label: 'Next 1 h' },
  { value: 120,  label: 'Next 2 h' },
  { value: 360,  label: 'Next 6 h' },
  { value: 720,  label: 'Next 12 h' },
  { value: 1440, label: 'Next 24 h' },
];

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-IN');
}

function formatRupees(pts) {
  if (pts === null || pts === undefined) return '—';
  return `₹${Number(pts).toLocaleString('en-IN')}`;
}

function formatMinutes(mins) {
  if (mins == null || isNaN(mins)) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}


export default function PointsAnalyticsPage() {
  const [summary, setSummary]           = useState(null);
  const [topUsers, setTopUsers]         = useState([]);
  const [expiring, setExpiring]         = useState([]);
  const [windowMin, setWindowMin]       = useState(120);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [lastError, setLastError]       = useState(null);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setLastError(null);
    try {
      const [s, t, e] = await Promise.all([
        adminApi.getPointsAnalyticsSummary(),
        adminApi.getPointsAnalyticsTopUsers(10),
        adminApi.getPointsAnalyticsExpiringSoon(windowMin, 20),
      ]);
      setSummary(s.data);
      setTopUsers(t.data.items || []);
      setExpiring(e.data.items || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load analytics';
      setLastError(typeof msg === 'string' ? msg : 'Failed to load analytics');
      toast.error(typeof msg === 'string' ? msg : 'Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowMin]);

  useEffect(() => { loadAll(false); }, [loadAll]);

  const asOf = useMemo(() => {
    if (!summary?.as_of) return null;
    try {
      return new Date(summary.as_of).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return summary.as_of; }
  }, [summary]);

  return (
    <AdminShell title="Points · Analytics">
      <div
        className="max-w-6xl mx-auto space-y-6"
        data-testid="admin-points-analytics-page"
      >
        {/* ─────────── Header ─────────── */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#F2F2F2] flex items-center gap-2">
              <BarChart3 size={20} className="text-[#D4A843]" /> Points Analytics
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              Live KPIs computed from the wallet ledger — no caching, refreshed on demand.
              {asOf && (
                <span className="text-[#6B6B6B] ml-1.5">
                  · As of <span className="font-mono">{asOf}</span> UTC
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
            onClick={() => loadAll(true)}
            disabled={refreshing}
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw size={14} className={refreshing ? 'mr-2 animate-spin' : 'mr-2'} />
            Refresh
          </Button>
        </div>

        {lastError && (
          <Card className="p-4 rounded-2xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.3)] flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#FF4D4D] mt-0.5" />
            <p className="text-sm text-[#FF8A8A]">{lastError}</p>
          </Card>
        )}

        {/* ─────────── KPI cards ─────────── */}
        <section data-testid="kpi-cards-section" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Issued Today"
            value={summary?.issued_today}
            suffix="pts"
            sub={`≈ ${formatRupees(summary?.issued_today)}`}
            icon={<Coins size={16} />}
            tone="gold"
            loading={loading}
            testId="kpi-issued-today"
          />
          <KpiCard
            label="Redeemed Today"
            value={summary?.redeemed_today}
            suffix="pts"
            sub={`≈ ${formatRupees(summary?.redeemed_today)}`}
            icon={<ArrowDownCircle size={16} />}
            tone="green"
            loading={loading}
            testId="kpi-redeemed-today"
          />
          <KpiCard
            label="Expired Today"
            value={summary?.expired_today}
            suffix="pts"
            sub={`≈ ${formatRupees(summary?.expired_today)}`}
            icon={<Hourglass size={16} />}
            tone="red"
            loading={loading}
            testId="kpi-expired-today"
          />
          <KpiCard
            label="Active Liability"
            value={summary?.active_liability}
            suffix="pts"
            sub={`≈ ${formatRupees(summary?.active_liability)}`}
            icon={<ShieldCheck size={16} />}
            tone="blue"
            loading={loading}
            testId="kpi-active-liability"
          />
        </section>

        {/* ─────────── Top users + Expiring soon (responsive grid) ─────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* TOP USERS */}
          <Card
            data-testid="top-users-card"
            className="p-5 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)]"
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-[#F2F2F2] flex items-center gap-2">
                <Crown size={16} className="text-[#D4A843]" /> Top Users by Active Points
              </h2>
              <Badge variant="outline" className="border-[rgba(255,255,255,0.1)] text-[#6B6B6B] text-[10px]">
                Top {topUsers.length || 10}
              </Badge>
            </header>

            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full bg-[rgba(255,255,255,0.04)]" />
                ))}
              </div>
            ) : topUsers.length === 0 ? (
              <EmptyState
                icon={<Users size={26} className="text-[#6B6B6B]" />}
                title="No users with active points"
                hint="As customers check in and earn cover-charge credits, they'll show up here."
              />
            ) : (
              <Table data-testid="top-users-table">
                <TableHeader>
                  <TableRow className="border-[rgba(255,255,255,0.06)] hover:bg-transparent">
                    <TableHead className="w-[42px] text-[#6B6B6B] text-[11px]">#</TableHead>
                    <TableHead className="text-[#6B6B6B] text-[11px]">Customer</TableHead>
                    <TableHead className="text-right text-[#6B6B6B] text-[11px]">Active pts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topUsers.map((u, idx) => (
                    <TableRow
                      key={u.user_id}
                      className="border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]"
                      data-testid={`top-user-row-${idx}`}
                    >
                      <TableCell>
                        <span
                          className={
                            'inline-flex w-6 h-6 items-center justify-center rounded-full text-[11px] font-mono ' +
                            (idx === 0
                              ? 'bg-[rgba(212,168,67,0.18)] text-[#D4A843] border border-[rgba(212,168,67,0.4)]'
                              : 'bg-[rgba(255,255,255,0.03)] text-[#B9B9B9] border border-[rgba(255,255,255,0.06)]')
                          }
                        >
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-[#F2F2F2] font-medium truncate" title={u.full_name}>
                          {u.full_name}
                        </p>
                        {u.phone && (
                          <p className="text-[11px] text-[#6B6B6B] flex items-center gap-1 mt-0.5">
                            <Phone size={9} /> {u.phone}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-semibold text-[#D4A843]" data-testid={`top-user-pts-${idx}`}>
                          {formatNumber(u.active_points)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* EXPIRING SOON */}
          <Card
            data-testid="expiring-soon-card"
            className="p-5 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)]"
          >
            <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-base font-semibold text-[#F2F2F2] flex items-center gap-2">
                <Hourglass size={16} className="text-[#FF8A8A]" /> Expiring Soon
              </h2>
              <div className="flex items-center gap-2">
                <Select
                  value={String(windowMin)}
                  onValueChange={(v) => setWindowMin(parseInt(v, 10))}
                >
                  <SelectTrigger
                    data-testid="expiring-window-select"
                    className="h-8 w-[130px] text-xs bg-[#111] border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#B9B9B9]">
                    {WINDOW_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </header>

            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full bg-[rgba(255,255,255,0.04)]" />
                ))}
              </div>
            ) : expiring.length === 0 ? (
              <EmptyState
                icon={<Clock size={26} className="text-[#6B6B6B]" />}
                title="No points expiring in this window"
                hint="Try widening the window or check back closer to a check-in surge."
              />
            ) : (
              <Table data-testid="expiring-soon-table">
                <TableHeader>
                  <TableRow className="border-[rgba(255,255,255,0.06)] hover:bg-transparent">
                    <TableHead className="text-[#6B6B6B] text-[11px]">Customer</TableHead>
                    <TableHead className="text-right text-[#6B6B6B] text-[11px]">Expiring</TableHead>
                    <TableHead className="text-right text-[#6B6B6B] text-[11px]">In</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiring.map((u, idx) => {
                    const urgent = (u.minutes_until_expiry ?? 999) <= 30;
                    return (
                      <TableRow
                        key={u.user_id}
                        className="border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]"
                        data-testid={`expiring-row-${idx}`}
                      >
                        <TableCell>
                          <p className="text-sm text-[#F2F2F2] font-medium truncate" title={u.full_name}>
                            {u.full_name}
                          </p>
                          {u.phone && (
                            <p className="text-[11px] text-[#6B6B6B] flex items-center gap-1 mt-0.5">
                              <Phone size={9} /> {u.phone}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono font-semibold text-[#FF8A8A]" data-testid={`expiring-pts-${idx}`}>
                            {formatNumber(u.expiring_points)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant="outline"
                            className={
                              urgent
                                ? 'border-[rgba(255,77,77,0.4)] text-[#FF8A8A] font-mono'
                                : 'border-[rgba(251,185,36,0.4)] text-[#FBB924] font-mono'
                            }
                          >
                            {formatMinutes(u.minutes_until_expiry)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </section>

        {/* Footnote */}
        <p className="text-[11px] text-[#6B6B6B] text-center pt-2">
          1 Point = ₹1 · Cover-charge points expire 12 h after credit · KPIs are recomputed live on every refresh.
        </p>
      </div>
    </AdminShell>
  );
}


// ─── KPI card ──────────────────────────────────────────────────────────────
function KpiCard({ label, value, suffix, sub, icon, tone, loading, testId }) {
  const palette = {
    gold:  { fg: '#D4A843', bg: 'rgba(212,168,67,0.06)',  border: 'rgba(212,168,67,0.22)' },
    green: { fg: '#4ADE80', bg: 'rgba(74,222,128,0.05)',  border: 'rgba(74,222,128,0.22)' },
    red:   { fg: '#FF8A8A', bg: 'rgba(255,77,77,0.05)',   border: 'rgba(255,77,77,0.22)' },
    blue:  { fg: '#60A5FA', bg: 'rgba(96,165,250,0.05)',  border: 'rgba(96,165,250,0.22)' },
  }[tone || 'gold'];
  return (
    <Card
      className="p-4 rounded-2xl border"
      style={{ background: palette.bg, borderColor: palette.border }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">{label}</p>
        <span style={{ color: palette.fg }}>{icon}</span>
      </div>
      {loading ? (
        <Skeleton className="h-10 w-24 bg-[rgba(255,255,255,0.06)]" />
      ) : (
        <p className="text-3xl font-bold font-mono leading-tight" style={{ color: palette.fg }}>
          {formatNumber(value)}
          {suffix && <span className="text-xs text-[#6B6B6B] font-normal ml-1">{suffix}</span>}
        </p>
      )}
      {!loading && sub && (
        <p className="text-[11px] text-[#6B6B6B] mt-1.5">{sub}</p>
      )}
    </Card>
  );
}


// ─── Empty state ──────────────────────────────────────────────────────────
function EmptyState({ icon, title, hint }) {
  return (
    <div className="text-center py-8 px-6" data-testid="analytics-empty-state">
      <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] mb-3">
        {icon}
      </div>
      <p className="text-sm text-[#F2F2F2] font-medium">{title}</p>
      {hint && <p className="text-xs text-[#6B6B6B] mt-1 max-w-xs mx-auto">{hint}</p>}
    </div>
  );
}
